import React, { useEffect, useRef, useState } from "react";
import { Prompt, useHistory, useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Skeleton,
  Space,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  PictureOutlined,
  SaveOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { shrinkPhoto } from "../workReports/photos";
import {
  createDoc,
  deleteDoc,
  deletePhoto,
  getDoc,
  getMeta,
  photoUrl,
  updateDoc,
  uploadPhoto
} from "./api";
import LinkFields from "./LinkFields";
import ReportDialog from "./ReportDialog";
import NotesField from "./NotesField";
import { applyChange } from "./links";
import OfflineNotice from "../../../offline/OfflineNotice";
import { useSyncedRecord } from "../../../offline/hooks";
import "./siteDocs.css";

const LIST = "/user/site-docs";
const LINKS = ["client", "clientBranch", "project", "offer", "invoice"];

const formFromDoc = doc => ({
  date: doc.date,
  title: doc.title || "",
  notes: doc.notes || "",
  ...Object.fromEntries(LINKS.map(key => [key, doc[key] ? String(doc[key]) : ""])),
  photoCaptions: {}
});

const bodyFromForm = form => ({
  date: form.date,
  title: form.title,
  notes: form.notes,
  ...Object.fromEntries(LINKS.map(key => [key, form[key] || null])),
  photoCaptions: form.photoCaptions
});

// Unsaved edits stay on this device until saved: there is often no signal on site.
const draftKey = id => `siteDoc:draft:${id}`;
const readDraft = id => {
  try {
    return JSON.parse(localStorage.getItem(draftKey(id)) || "null");
  } catch {
    return null;
  }
};
const writeDraft = (id, value) => {
  try {
    if (value) localStorage.setItem(draftKey(id), JSON.stringify(value));
    else localStorage.removeItem(draftKey(id));
  } catch {
    // storage full or blocked: the edit is still on screen
  }
};

const todayBerlin = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(new Date());

const SiteDocEditor = () => {
  const { t } = useTranslation("dashboard");
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const isNew = id === "new";
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  // Set right before navigating away on purpose (after a create or delete)
  const leaving = useRef(false);
  // The id just created: the route changes to it, the data is already here
  const justCreated = useRef(null);
  const [meta, setMeta] = useState(null);
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [restored, setRestored] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  // Created offline: moves to the real address once uploaded
  useSyncedRecord(id, serverId => history.replace(`${LIST}/${serverId}`), { when: !dirty });

  const applyDoc = data => {
    setDoc(data);
    setForm(formFromDoc(data));
    setDirty(false);
    writeDraft(data._id, null);
  };

  useEffect(() => {
    if (justCreated.current === id) return;
    let cancelled = false;
    (async () => {
      try {
        const [metaRes, docRes] = await Promise.all([getMeta(), isNew ? null : getDoc(id)]);
        if (cancelled) return;
        // Read before applyDoc, which clears it
        const local = readDraft(id);
        setMeta(metaRes.data);
        let base;
        if (isNew) {
          // Prefilled from a link: /user/site-docs/new?client=…&project=…
          const params = new URLSearchParams(location.search);
          base = { date: todayBerlin(), title: "", notes: "", photoCaptions: {} };
          for (const key of LINKS) base[key] = "";
          for (const key of ["client", "project", "offer", "invoice"]) {
            if (params.get(key))
              base = { ...base, ...applyChange(base, key, params.get(key), metaRes.data) };
          }
          setDoc({ photos: [], can: { update: true, delete: false } });
          setForm(base);
        } else {
          applyDoc(docRes.data.data);
          base = formFromDoc(docRes.data.data);
        }
        const savedAt = isNew ? 0 : Date.parse(docRes.data.data.updatedAt);
        if (local?.form && local.savedAt > savedAt && (isNew || docRes.data.data.can.update)) {
          setForm({ ...base, ...local.form, photoCaptions: local.form.photoCaptions || {} });
          setDirty(true);
          setRestored(true);
        } else if (local) {
          writeDraft(id, null);
        }
      } catch (error) {
        generalErrorAlert(error);
        history.push(LIST);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (dirty && form) writeDraft(id, { form, savedAt: Date.now() });
  }, [dirty, form]);

  const update = patch => {
    setForm(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const appendNotes = text => {
    if (!text) return;
    setForm(prev => {
      const current = prev.notes || "";
      const glue = !current
        ? ""
        : /\n$/.test(current)
          ? ""
          : /[.!?]$/.test(current.trim())
            ? " "
            : ". ";
      return { ...prev, notes: `${current}${glue}${text}` };
    });
    setDirty(true);
  };

  /** Saves the form; on a new entry creates it and moves to its address. Returns the id. */
  const save = async ({ quiet = false } = {}) => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await createDoc(bodyFromForm(form));
        const created = res.data.data;
        writeDraft("new", null);
        applyDoc(created);
        justCreated.current = created._id;
        leaving.current = true;
        history.replace(`${LIST}/${created._id}`);
        leaving.current = false;
        if (!quiet) Success(t("Saved"));
        return created._id;
      }
      const res = await updateDoc(id, bodyFromForm(form));
      applyDoc(res.data.data);
      if (!quiet) Success(t("Saved"));
      return id;
    } catch (error) {
      generalErrorAlert(error);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const addPhotos = async files => {
    const list = Array.from(files || []);
    if (!list.length) return;
    // Photos need a saved entry; unsaved text is saved with it
    const targetId = isNew || dirty ? await save({ quiet: true }) : id;
    if (!targetId) return;
    setUploading(list.length);
    let latest = null;
    for (const file of list) {
      try {
        const { blob, width, height } = await shrinkPhoto(file);
        const res = await uploadPhoto(targetId, blob, {
          width,
          height,
          takenAt: file.lastModified
        });
        latest = res.data.data;
      } catch (error) {
        generalErrorAlert(error);
      } finally {
        setUploading(n => Math.max(0, n - 1));
      }
    }
    if (latest) setDoc(latest);
  };

  const removePhoto = photoId =>
    Modal.confirm({
      title: t("Remove this photo?"),
      okText: t("Remove"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          const res = await deletePhoto(id, photoId);
          setDoc(res.data.data);
        } catch (error) {
          generalErrorAlert(error);
        }
      }
    });

  const remove = () =>
    Modal.confirm({
      title: t("Delete this documentation?"),
      content: t("The note and all its photos are deleted. This action cannot be undone."),
      okText: t("Delete"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await deleteDoc(id);
          writeDraft(id, null);
          leaving.current = true;
          history.push(LIST);
        } catch (error) {
          generalErrorAlert(error);
        }
      }
    });

  const discardRestored = () => {
    writeDraft(id, null);
    setRestored(false);
    if (isNew) {
      setForm(prev => ({
        ...prev,
        title: "",
        notes: "",
        ...Object.fromEntries(LINKS.map(key => [key, ""]))
      }));
      setDirty(false);
    } else {
      applyDoc(doc);
    }
  };

  if (!meta || !form || !doc) {
    return (
      <div className="site-docs-page">
        <Card>
          <Skeleton active />
        </Card>
      </div>
    );
  }

  const readOnly = !doc.can?.update;
  const photos = doc.photos || [];
  const canSave = !readOnly && (dirty || isNew);
  const hasContent = Boolean(
    form.notes.trim() || form.title.trim() || form.client || photos.length
  );

  return (
    <div className="site-docs-page sd-editor">
      <Prompt
        when={dirty && !readOnly}
        message={() =>
          leaving.current ||
          t("You have unsaved changes. Leave the page and keep them on this device?")
        }
      />

      <div className="sd-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          aria-label={t("Back")}
          onClick={() => history.push(LIST)}
        />
        <div className="sd-header-title">
          <Typography.Title level={4}>
            {isNew ? t("New documentation") : `${t("Documentation")} ${doc.number}`}
          </Typography.Title>
          {!isNew && doc.createdByName && (
            <Typography.Text type="secondary" className="sd-small">
              {t("by {{name}}", { name: doc.createdByName })}
            </Typography.Text>
          )}
        </div>
        {!isNew && (
          <Button
            icon={<FilePdfOutlined />}
            aria-label={t("Photo report (PDF)")}
            disabled={dirty}
            title={dirty ? t("Save first, then create the PDF.") : t("Photo report (PDF)")}
            onClick={() => setReportOpen(true)}
          >
            <span className="sd-hide-phone">PDF</span>
          </Button>
        )}
        <Space wrap className="sd-header-actions sd-hide-phone">
          {!isNew && doc.can?.delete && (
            <Button danger icon={<DeleteOutlined />} onClick={remove}>
              {t("Delete")}
            </Button>
          )}
          {!readOnly && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!canSave || (isNew && !hasContent)}
              onClick={() => save()}
            >
              {t("Save")}
            </Button>
          )}
        </Space>
      </div>

      {!isNew && <OfflineNotice module="site-docs" record={doc} />}
      {restored && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t("Unsaved changes from this device were restored.")}
          action={
            <Button size="small" onClick={discardRestored}>
              {t("Discard")}
            </Button>
          }
        />
      )}
      {readOnly && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t("You can view this documentation but not change it.")}
        />
      )}

      <Card className="mb-3 sd-section" title={t("For whom is this?")}>
        <LinkFields
          catalog={meta}
          disabled={readOnly}
          value={form}
          onChange={next => update(next)}
        />
      </Card>

      <Card className="mb-3 sd-section" title={t("What was done")}>
        <Form layout="vertical" component="div">
          <div className="sd-row">
            <Form.Item label={t("Date")} className="sd-date">
              <DatePicker
                className="sd-full"
                format="DD.MM.YYYY"
                allowClear={false}
                disabled={readOnly}
                inputReadOnly
                value={form.date ? dayjs(form.date) : null}
                onChange={value => value && update({ date: value.format("YYYY-MM-DD") })}
              />
            </Form.Item>
            <Form.Item label={t("Title (optional)")} className="sd-grow">
              <Input
                value={form.title}
                disabled={readOnly}
                maxLength={200}
                placeholder={t("e.g. Bathroom demolition, day 1")}
                onChange={event => update({ title: event.target.value })}
              />
            </Form.Item>
          </div>
          <Form.Item label={t("Notes")}>
            <NotesField
              value={form.notes}
              disabled={readOnly}
              voiceConfigured={Boolean(meta.voiceConfigured)}
              voiceTranslateTo={meta.voiceTranslateTo}
              onChange={notes => update({ notes })}
              onAppend={appendNotes}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card className="mb-3 sd-section" title={`${t("Photos")} (${photos.length})`}>
        {!readOnly && (
          <Space wrap className="sd-photo-actions">
            <Button
              type="primary"
              icon={<CameraOutlined />}
              loading={uploading > 0}
              onClick={() => cameraInput.current?.click()}
            >
              {uploading > 0 ? t("Uploading {{n}} …", { n: uploading }) : t("Take photo")}
            </Button>
            <Button
              icon={<PictureOutlined />}
              disabled={uploading > 0}
              onClick={() => galleryInput.current?.click()}
            >
              {t("From gallery")}
            </Button>
          </Space>
        )}
        {/* The camera input opens the camera directly on phones; desktops show a file dialog */}
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={event => {
            addPhotos(event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={galleryInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          hidden
          onChange={event => {
            addPhotos(event.target.files);
            event.target.value = "";
          }}
        />
        {photos.length ? (
          <div className="sd-photo-grid">
            <Image.PreviewGroup>
              {photos.map(photo => (
                <figure key={photo._id}>
                  <Image src={photoUrl(doc._id, photo._id)} alt={photo.caption || ""} />
                  <Input
                    size="small"
                    placeholder={t("Caption")}
                    disabled={readOnly}
                    maxLength={300}
                    value={form.photoCaptions[photo._id] ?? photo.caption}
                    onChange={event =>
                      update({
                        photoCaptions: { ...form.photoCaptions, [photo._id]: event.target.value }
                      })
                    }
                  />
                  {photo.takenAt && (
                    <figcaption>{dayjs(photo.takenAt).format("DD.MM.YYYY HH:mm")}</figcaption>
                  )}
                  {!readOnly && (
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("Remove")}
                      className="sd-photo-remove"
                      onClick={() => removePhoto(photo._id)}
                    />
                  )}
                </figure>
              ))}
            </Image.PreviewGroup>
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("Before and after pictures show what was done.")}
          />
        )}
      </Card>

      {!isNew && (
        <ReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          selection={{ ids: [doc._id] }}
          count={1}
          defaultTitle={doc.title || ""}
          defaultEmail={
            (meta.clients || []).find(c => String(c._id) === String(doc.client || ""))?.email || ""
          }
          canEmail={meta.can?.email}
        />
      )}

      {/* Phones: the main actions stay reachable at the bottom of the screen */}
      {!readOnly && (
        <div className="sd-savebar">
          {!isNew && doc.can?.delete && (
            <Button danger icon={<DeleteOutlined />} aria-label={t("Delete")} onClick={remove} />
          )}
          <Button
            icon={<CameraOutlined />}
            loading={uploading > 0}
            onClick={() => cameraInput.current?.click()}
          >
            {t("Photo")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!canSave || (isNew && !hasContent)}
            onClick={() => save()}
            className="sd-grow"
          >
            {t("Save")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SiteDocEditor;
